import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Info, Check, Minus, X } from "lucide-react";

const COLORS = {
  green: { fg: "#16a34a", bg: "rgba(22,163,74,0.12)", ring: "rgba(22,163,74,0.35)" },
  amber: { fg: "#d97706", bg: "rgba(217,119,6,0.12)", ring: "rgba(217,119,6,0.35)" },
  red: { fg: "#dc2626", bg: "rgba(220,38,38,0.12)", ring: "rgba(220,38,38,0.35)" },
};

function healthState(f) {
  if (!f.active) return { icon: Minus, color: "#94a3b8" };
  if (f.health >= 0.7) return { icon: Check, color: "#16a34a" };
  if (f.health >= 0.4) return { icon: Minus, color: "#d97706" };
  return { icon: X, color: "#dc2626" };
}

export default function ReliabilityBadge({ rel, lang = "it", compact = false }) {
  const L = (it, en) => (lang === "en" ? en : it);
  if (!rel || typeof rel.score !== "number") return null;
  const c = COLORS[rel.color] || COLORS.amber;
  const Icon = rel.color === "green" ? ShieldCheck : rel.color === "red" ? ShieldAlert : ShieldQuestion;
  const title = L("Affidabilità del consiglio", "Advice reliability");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="reliability-badge"
          className={`inline-flex items-center gap-1.5 rounded-full font-bold transition-colors ${compact ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}
          style={{ backgroundColor: c.bg, color: c.fg, boxShadow: `inset 0 0 0 1px ${c.ring}` }}
          title={title}
        >
          <Icon className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
          {compact ? `${rel.score}%` : `${title}: ${rel.score}%`}
          <Info className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="reliability-popover">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: c.bg }}>
            <Icon className="w-5 h-5" style={{ color: c.fg }} />
          </span>
          <div>
            <p className="text-sm font-bold leading-none" style={{ color: c.fg }}>{rel.score}% · {title}</p>
            {rel.last_updated_age_days != null && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {L("Ultimo aggiornamento", "Last update")}: {rel.last_updated_age_days === 0
                  ? L("oggi", "today")
                  : L(`${Math.round(rel.last_updated_age_days)} giorni fa`, `${Math.round(rel.last_updated_age_days)} days ago`)}
              </p>
            )}
          </div>
        </div>
        {rel.summary && <p className="text-xs text-muted-foreground mb-3 leading-snug">{rel.summary}</p>}
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
          {L("Fattori del punteggio", "Score factors")}
        </p>
        <ul className="space-y-1.5" data-testid="reliability-factors">
          {(rel.factors || []).map((f) => {
            const hs = healthState(f);
            const HIcon = hs.icon;
            return (
              <li key={f.key} className="flex items-start gap-2 text-xs" data-testid={`reliability-factor-${f.key}`}>
                <HIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: hs.color }} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{f.label}</span>
                    <span className="text-[10px] font-data px-1.5 py-0.5 rounded" style={{ backgroundColor: `${hs.color}1f`, color: hs.color }}>
                      {f.status}
                    </span>
                  </span>
                  <span className="text-[11px] text-muted-foreground block">{f.detail}</span>
                  <span className="text-[10px] text-muted-foreground/70">{L("peso", "weight")} {f.weight}%</span>
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-muted-foreground/70 mt-3 leading-snug">
          {L("Indica quanto il consiglio è affidabile (dati aggiornati e completi), non quanto è certo che il prezzo sia corretto.",
             "Shows how reliable the advice is (fresh, complete data), not how certain the price is correct.")}
        </p>
      </PopoverContent>
    </Popover>
  );
}
